import { Popover, PopoverContent, PopoverTrigger, Portal } from '@chakra-ui/react';
import { Calendar, CalendarTabs } from '@sketchnote/shared-components';
import { format, isValid, parseISO } from 'date-fns';
import React, { useRef, useState } from 'react';
import { OnChangeDateRangeCallback } from 'react-calendar';
import { useTableStore } from 'src/features/list/store';
import { Task } from 'src/features/tasks/types/tasks';
import useOnClickOutside from 'use-onclickoutside';

interface DateRangePickerProps {
  value: [Date] | [Date, Date] | null;
  onChange: OnChangeDateRangeCallback;
  onDoneClick: () => void;
  inputClassName?: string;
  placeholderText?: string;
  dateFormat?: string;
  initialTab?: CalendarTabs;
}

export const DateFieldCell = ({
  task,
  fieldId,
  data,
  api,
}: {
  task: Partial<Task>;
  fieldId: string;
  data: any;
  api: any;
}): JSX.Element => {
  const dateToSet =
    Array.isArray(data) && data.length <= 2
      ? data
          .map((date) => {
            // check if date is valid
            const parsedDate = parseISO(date); //parse(date, 'P', new Date(), { locale: enGB });
            const isValidDate = isValid(parsedDate);
            return isValidDate ? parseISO(date) : new Date();
          })
          .filter((date) => !!date)
      : [];

  const myRef = useRef(null);

  const [dates, setDates] = useState<Date[] | [Date, Date] | null>(dateToSet);

  const onUpdateCustomFieldData = useTableStore((s) => s.onUpdateCustomFieldData);

  const handleChange = (values) => {
    if (values.length) {
      values.map((date) => (date ? parseISO(date) : undefined)).filter((date) => !!date);
    }
    setDates(values);
  };

  const handleOnDone = () => {
    if (task?.id) {
      onUpdateCustomFieldData(task, fieldId, dates);
    }
    api.stopEditing();
  };

  useOnClickOutside(myRef, () => {
    api.stopEditing();
  });

  return (
    <div className="flex rounded-sm items-center" ref={myRef}>
      <DateRangePicker
        value={dates !== null ? (dates.length > 0 ? ([...dates] as [Date]) : null) : null}
        onChange={handleChange}
        onDoneClick={handleOnDone}
        inputClassName="date-picker flex items-center bg-white w-full rounded-sm h-36 text-left"
        initialTab={dates?.length === 1 ? 'startEndDate' : 'date'}
      />
    </div>
  );
};

const DateRangePicker = ({
  value,
  onChange,
  onDoneClick,
  placeholderText = '',
  dateFormat = 'do MMM yyyy',
  initialTab = 'date',
}: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isReadOnly } = useTableStore();
  const open = () => setIsOpen(!isOpen);
  const close = () => setIsOpen(false);

  const handleDatesForInput = (values) => {
    if (!values) return placeholderText;
    else if (values.length === 1) return `${format(values[0], dateFormat)}`;
    else if (values.length > 1)
      return `${format(values[0], dateFormat)} - ${format(values[1], dateFormat)}`;
    return '';
  };

  const handleDoneClick = () => {
    onDoneClick();
    setIsOpen(false);
  };

  return (
    <Calendar value={value} onChange={onChange} onDone={handleDoneClick} initialTab={initialTab} />
  );
};
